import { Vue, Component } from 'vue-property-decorator';
import createAuth0Client, {
  PopupLoginOptions,
  Auth0Client,
  RedirectLoginOptions,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  LogoutOptions,
} from '@auth0/auth0-spa-js';
import { User } from './User';

export type Auth0Options = {
  domain: string;
  clientId: string;
  audience?: string;
  [key: string]: any;
};

export type RedirectCallback = (appState: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any

class ManagedPromise {
  resolve: ((value: string) => void) | undefined = undefined;
  promise = new Promise<string>(resolve => {
    this.resolve = resolve;
  });
}

@Component({})
export class VueAuth extends Vue {
  loading = true;
  isAuthenticated? = false;
  user?: User;
  auth0Client?: Auth0Client;
  popupOpen = false;
  error?: Error;
  fetchTokenPromises: ManagedPromise[] = [];

  async getUser() {
    return new User(await this.auth0Client?.getUser());
  }

  /** Authenticates the user using a popup window */
  async loginWithPopup(o: PopupLoginOptions) {
    this.popupOpen = true;

    try {
      await this.auth0Client?.loginWithPopup(o);
    } catch (e: any) {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      console.error(e);
      this.error = e;
    } finally {
      this.popupOpen = false;
    }

    this.user = await this.getUser();
    this.isAuthenticated = true;
  }

  /** Authenticates the user using the redirect method */
  loginWithRedirect(o: RedirectLoginOptions) {
    // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
    return this.auth0Client?.loginWithRedirect(o);
  }

  /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
  async getTokenSilently(o: GetTokenSilentlyOptions) {
    // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
    // only allow 1 api call to `getTokenSilently` at a time
    // we will create promises for each subsequent calls, put it in the array
    // and resolve all of them when first api call is resolved
    if (this.fetchTokenPromises.length !== 0 || !this.isAuthenticated) {
      const managedPromise: ManagedPromise = new ManagedPromise();
      this.fetchTokenPromises.push(managedPromise);
      return managedPromise.promise;
    }
    return this._completeGetTokenSilently(o);
  }

  async _completeGetTokenSilently(o: GetTokenSilentlyOptions) {
    const result = await this.auth0Client?.getTokenSilently(o);
    this.fetchTokenPromises.forEach(promise => {
      promise.resolve && promise.resolve(result || '');
    });
    this.fetchTokenPromises = [];
    return result;
  }

  /** Logs the user out and removes their session on the authorization server */
  logout(o: LogoutOptions) {
    // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
    return this.auth0Client?.logout(o);
  }

  /** Use this lifecycle method to instantiate the SDK client */
  async init(
    onRedirectCallback: RedirectCallback,
    redirectUri: string,
    auth0Options: Auth0Options
  ) {
    // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
    // Create a new instance of the SDK client using members of the given options object
    this.auth0Client = await createAuth0Client({
      domain: auth0Options.domain,
      client_id: auth0Options.clientId,
      audience: auth0Options.audience,
      redirect_uri: redirectUri,
      useRefreshTokens: true,
      advancedOptions: auth0Options.advancedOptions,
      cacheLocation: auth0Options.cacheLocation,
    });

    try {
      // If the user is returning to the app after authentication..
      if (
        window.location.search.includes('error=') ||
        (window.location.search.includes('code=') &&
          window.location.search.includes('state='))
      ) {
        // handle the redirect and retrieve tokens
        const { appState } =
          (await this.auth0Client?.handleRedirectCallback()) ?? {
            appState: undefined,
          };

        // Notify subscribers that the redirect callback has happened, passing the appState
        // (useful for retrieving any pre-authentication state)
        onRedirectCallback(appState);
      }
    } catch (e: any) {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      console.error(e);
      this.error = e;
    } finally {
      // Initialize our internal authentication state when the page is reloaded
      this.isAuthenticated = await this.auth0Client?.isAuthenticated();
      this.user = await this.getUser();
      this.loading = false;
      if (this.isAuthenticated) {
        this._completeGetTokenSilently({});
      }
    }
  }
}
